import { CreatedProjects, ProjectDetailsStepState, ProjectRow } from "../../../../../models";
import { bulkCreateProjects, createGroup } from "../../../../../service/project";
import { ServiceError, Status } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";
import { ERROR_REQUEST_TIMEOUT } from "../../../../../utils/errorConstants";
import { buildCreateRequests, getPendingRows, ProjectCreateRequest } from "./buildCreateRequests";
import { LANDHOLDING_ERROR_SCOPE } from "./createProjectErrors";
import { mapManualRowToCreateProjectRequest, mapRowToBulkProject } from "./projectRowMappers";

export type SubmitProjectDetailsResult = {
  // Straight from the api, each stamped with the uuid of the row it belongs to. Callers decide how
  // to show them: a summary list, or against the row or field they came from.
  errors: ServiceError[];
  // Everything written so far - what this call wrote merged over what an earlier save had
  // recorded. Empty on a validate-only call: it reserves no uuid and resolves nothing (BR-02891).
  created: CreatedProjects;
};

// A Status.Error response without structured errors (a timeout, a 5xx) must still read as a
// failure - callers treat an empty errors array as success, so an empty array here would let the
// journey advance, or report a save as done, on a request that never went through.
const fallbackSubmitError: ServiceError = {
  code: "PROJECT_DETAILS_SUBMIT_FAILED",
  message: "Something went wrong and your projects could not be saved. Please try again.",
};

// A timeout is not a refusal: the web stopped waiting, and the api may still have gone on to
// finish. What that means depends on the call. A validate-only call persisted nothing either way,
// so it can simply be sent again; a save may have been written in full after the web gave up, and a
// blind retry would then collide with it (BR-02888) - so the person is told to check first.
const timedOutSubmitError = (validateOnly: boolean): ServiceError => {
  const code = validateOnly ? "PROJECT_DETAILS_VALIDATE_TIMED_OUT" : "PROJECT_DETAILS_SUBMIT_TIMED_OUT";
  return { code, message: getErrorMessageFromCode(code) };
};

// What a refused create request reads as to the journey: the api's own errors where it gave any,
// the timeout's where the web gave up, and the fallback where it was refused without saying why.
const refusalErrors = (errors: ServiceError[] | undefined, validateOnly: boolean): ServiceError[] => {
  if (errors?.some((error) => error.code === ERROR_REQUEST_TIMEOUT)) return [timedOutSubmitError(validateOnly)];
  return errors && errors.length > 0 ? errors : [fallbackSubmitError];
};

// The api stamps a member error with the row's own uuid when it can (BR-02888); when it cannot -
// an older api build - it still names the row by its position in the request, as
// "Projects[i].Property". Resolved here, against the rows this one request sent, because the views
// downstream only know the step's whole row list, in which that position means a different row.
const rowIndexPattern = /Projects\[(\d+)\]/i;

const stampErrorsToRows = (
  errors: ServiceError[],
  sentRows: ProjectRow[],
  landholdingErrorRowKey: string | undefined
): ServiceError[] =>
  errors.map((error) => {
    if (error.parameters?.uuid) return error;

    const match = rowIndexPattern.exec(error.path ?? error.property ?? "");
    const index = match ? Number(match[1]) : -1;
    if (index >= 0 && index < sentRows.length) {
      return { ...error, parameters: { ...error.parameters, uuid: sentRows[index].key } };
    }

    // No row of its own: it is about the landholding itself. An uploaded landholding has no form to
    // show that on, so it is given a row to be shown against; a typed one keeps the landholding
    // form's own fields.
    if (!landholdingErrorRowKey) return error;

    return {
      ...error,
      parameters: { ...error.parameters, uuid: landholdingErrorRowKey, scope: LANDHOLDING_ERROR_SCOPE },
    };
  });

type RequestOutcome = {
  errors: ServiceError[];
  projectUuidByKey: Record<string, string>;
  groupUuid?: string;
};

const runGroupRequest = async (
  request: Extract<ProjectCreateRequest, { kind: "group" }>,
  rows: ProjectRow[],
  resolveDeveloperUuid: (projectKey: string) => string | null,
  validateOnly: boolean
): Promise<RequestOutcome> => {
  const { landholding } = request;
  const response = await createGroup({
    name: landholding.name,
    externalReference: landholding.referenceId,
    externalParty: landholding.reference ?? "",
    addressRegion: landholding.region || null,
    addressCountryCode: landholding.countryCode || null,
    locationCoordinates:
      landholding.latitude || landholding.longitude
        ? `${landholding.latitude || ""},${landholding.longitude || ""}`
        : null,
    type: "Landholding",
    projects: rows.map((row) => mapManualRowToCreateProjectRequest(row, resolveDeveloperUuid(row.key))),
    validateOnly,
  });

  if (response.status === Status.Error) {
    return { errors: refusalErrors(response.errors, validateOnly), projectUuidByKey: {} };
  }

  // The api persists the uuid each row was sent with (BR-02888), so the mapping back is the row's
  // own key - nothing has to be read off the response.
  return {
    errors: [],
    projectUuidByKey: validateOnly ? {} : Object.fromEntries(rows.map((row) => [row.key, row.key])),
    groupUuid: validateOnly ? undefined : response.data?.uuid,
  };
};

const runBulkRequest = async (
  rows: ProjectRow[],
  resolveDeveloperUuid: (projectKey: string) => string | null,
  validateOnly: boolean
): Promise<RequestOutcome> => {
  const response = await bulkCreateProjects({
    projects: rows.map((row) => mapRowToBulkProject(row, resolveDeveloperUuid(row.key))),
    validateOnly,
  });

  if (response.status === Status.Error) {
    return { errors: refusalErrors(response.errors, validateOnly), projectUuidByKey: {} };
  }

  // Results come back in the order the rows were sent; the uuid is the row's own either way.
  const createdRows = response.data?.projects ?? [];
  return {
    errors: [],
    projectUuidByKey: validateOnly
      ? {}
      : Object.fromEntries(rows.map((row, index) => [row.key, createdRows[index]?.projectUuid ?? row.key])),
  };
};

/*
  The one place the journey turns its rows into a create. Step 1 sends it with validateOnly true on
  Next and Step 2 with false on Save, so what is checked on the way through is exactly what is
  written at the end - the two cannot drift into validating one payload and saving another.

  The rows become one request per landholding plus one bulk request for the standalone rows
  (buildCreateRequests), all sent at once. Each is its own transaction on the api - a group and its
  members in one savepoint, the bulk set in another (BR-02882) - so on a save one can be written
  while another is refused. What was written is returned with the errors, for the caller to keep:
  passed back in as `created` on the retry, it is what decides which requests are sent again and
  which are skipped, so nothing is posted twice.
*/
export const callProjectDetails = async (
  details: ProjectDetailsStepState,
  // Per project, because each one is created with its own developer: Step 2's access list can name
  // a different full-access Developer for each of the session's projects.
  resolveDeveloperUuid: (projectKey: string) => string | null,
  validateOnly: boolean,
  created: CreatedProjects | null = null
): Promise<SubmitProjectDetailsResult> => {
  const pending = buildCreateRequests(details)
    .map((request) => ({ request, rows: getPendingRows(request, created) }))
    .filter(({ rows }) => rows.length > 0);

  // Settled rather than raced: a request that throws (a lost connection, a gateway timeout) must
  // not throw away what the others wrote - those are recorded, and only this one reads as failed.
  const outcomes = await Promise.allSettled(
    pending.map(({ request, rows }) =>
      request.kind === "group"
        ? runGroupRequest(request, rows, resolveDeveloperUuid, validateOnly)
        : runBulkRequest(rows, resolveDeveloperUuid, validateOnly)
    )
  );

  const errors: ServiceError[] = [];
  const projectUuidByKey = { ...created?.projectUuidByKey };
  const groupUuidByKey = { ...created?.groupUuidByKey };

  outcomes.forEach((outcome, index) => {
    const { request, rows } = pending[index];
    const landholdingErrorRowKey = request.kind === "group" ? request.landholdingErrorRowKey : undefined;

    if (outcome.status === "rejected") {
      errors.push(...stampErrorsToRows([fallbackSubmitError], rows, landholdingErrorRowKey));
      return;
    }

    errors.push(...stampErrorsToRows(outcome.value.errors, rows, landholdingErrorRowKey));
    Object.assign(projectUuidByKey, outcome.value.projectUuidByKey);
    if (outcome.value.groupUuid) groupUuidByKey[request.key] = outcome.value.groupUuid;
  });

  const groupUuids = Object.values(groupUuidByKey);

  return {
    errors,
    created: {
      projectUuidByKey,
      groupUuidByKey,
      groupUuid: groupUuids.length === 1 ? groupUuids[0] : undefined,
    },
  };
};

export const hasCreatedAnything = (created: CreatedProjects): boolean =>
  Object.keys(created.projectUuidByKey).length > 0 || Object.keys(created.groupUuidByKey).length > 0;
